import { useEffect, useMemo, useState } from "react";
import { Archive, Search, RotateCcw, Eye, CalendarDays, UserRound, Euro, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, pct } from "../../lib/format";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import DevisBuilder from "../devis/DevisBuilder";

export default function ArchivesView() {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [famille, setFamille] = useState("Toutes");
  const [selected, setSelected] = useState(null);

  const loadArchives = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/archives");
      setArchives(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Impossible de charger les archives"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArchives();
  }, []);

  const familles = useMemo(() => {
    return [
      "Toutes",
      ...Array.from(
        new Set(
          archives
            .map((d) => d.famille)
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "fr")),
    ];
  }, [archives]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return archives.filter((d) => {
      const matchesSearch =
        !q ||
        [
          d.numero,
          d.client_nom,
          d.chantier_nom,
          d.famille,
          d.commercial_responsable,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));

      const matchesFamille =
        famille === "Toutes" || d.famille === famille;

      return matchesSearch && matchesFamille;
    });
  }, [archives, search, famille]);

  const totalHt = filtered.reduce(
    (sum, d) => sum + Number(d.total_ht || 0),
    0
  );

  const totalMarge = filtered.reduce(
    (sum, d) => sum + Number(d.marge_brute_ht || 0),
    0
  );

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleDateString("fr-FR");
  };

  return (
    <div className="min-h-full bg-[#F5F9FD] p-6">
      <div className="max-w-[1600px] mx-auto space-y-6">

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-slate-900 flex items-center justify-center">
                <Archive className="h-5 w-5 text-white" />
              </div>

              <div>
                <h1 className="text-2xl font-bold text-[#102A4C]">
                  Archives
                </h1>
                <p className="text-sm text-[#66809E]">
                  Dossiers clôturés, conservés pour consultation et mémoire commerciale.
                </p>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={loadArchives}
            disabled={loading}
            className="bg-white border-slate-200"
          >
            <RotateCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kpi
            icon={Archive}
            label="Dossiers archivés"
            value={filtered.length}
          />

          <Kpi
            icon={Euro}
            label="CA HT archivé"
            value={eur(totalHt)}
          />

          <Kpi
            icon={TrendingUp}
            label="Marge brute archivée"
            value={eur(totalMarge)}
          />
        </div>

        <div className="bg-white rounded-2xl border border-[#E3EBF5] p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher client, numéro, chantier, famille..."
                className="pl-9 bg-white border-slate-200"
              />
            </div>

            <select
              value={famille}
              onChange={(e) => setFamille(e.target.value)}
              className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              {familles.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E3EBF5] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              Chargement des archives...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Archive className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <div className="font-semibold text-slate-600">
                Aucune archive trouvée
              </div>
              <div className="text-sm text-slate-400 mt-1">
                Les dossiers clôturés apparaîtront automatiquement ici.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-3">Dossier</th>
                    <th className="px-5 py-3">Client</th>
                    <th className="px-5 py-3">Chantier</th>
                    <th className="px-5 py-3">Famille</th>
                    <th className="px-5 py-3 text-right">HT</th>
                    <th className="px-5 py-3 text-right">Marge</th>
                    <th className="px-5 py-3">État</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Commercial</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filtered.map((d) => (
                    <tr
                      key={d.id}
                      className="hover:bg-slate-50/80 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="font-semibold text-[#102A4C]">
                          {d.numero || "Sans numéro"}
                        </div>

                        {d.titre && (
                          <div className="text-xs text-slate-400 mt-1">
                            {d.titre}
                          </div>
                        )}
                      </td>

                      <td className="px-5 py-4 font-medium text-slate-700">
                        {d.client_nom || "—"}
                      </td>

                      <td className="px-5 py-4 text-slate-600">
                        {d.chantier_nom || "—"}
                      </td>

                      <td className="px-5 py-4">
                        {d.famille ? (
                          <span className="inline-flex px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-medium">
                            {d.famille}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td className="px-5 py-4 text-right font-mono-metric font-semibold text-[#102A4C]">
                        {eur(d.total_ht || 0)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="font-mono-metric font-semibold text-emerald-600">
                          {eur(d.marge_brute_ht || 0)}
                        </div>

                        {d.total_ht > 0 && (
                          <div className="text-[11px] text-slate-400">
                            {pct(
                              ((Number(d.marge_brute_ht || 0) /
                                Number(d.total_ht)) *
                                100)
                            )}
                          </div>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        {d.statut === "Refusé" ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-xs font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                            Refusé
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Clôturé
                          </span>
                        )}
                      </td>

                      <td className="px-5 py-4 text-slate-600">
                        <div className="flex items-center gap-2">
                          <CalendarDays className="h-4 w-4 text-slate-400" />
                          {formatDate(
                            d.statut === "Refusé"
                              ? (d.date_refus || d.date_archivage)
                              : (d.date_cloture || d.date_archivage)
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-slate-600">
                        <div className="flex items-center gap-2">
                          <UserRound className="h-4 w-4 text-slate-400" />
                          {d.cloture_par || d.commercial_responsable || "—"}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelected(d)}
                            className="bg-white border-slate-200"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Consulter
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const confirmed = window.confirm(
                                `Restaurer le dossier ${d.numero || ""} ?\n\nIl redeviendra modifiable et sortira des archives.`
                              );

                              if (!confirmed) return;

                              try {
                                await api.post(`/devis/${d.id}/restaurer`);
                                toast.success("Dossier restauré avec succès");
                                await loadArchives();
                              } catch (error) {
                                toast.error(
                                  error?.response?.data?.detail ||
                                  "Impossible de restaurer le dossier"
                                );
                              }
                            }}
                            className="bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Restaurer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="text-xs text-slate-400">
          Les archives sont conservées dans le CRM et ne sont pas supprimées.
        </div>
      </div>

      {selected && (
        <DevisBuilder
          open={Boolean(selected)}
          onOpenChange={(open) => {
            if (!open) setSelected(null);
          }}
          clients={[]}
          catalogue={[]}
          devis={selected}
          readOnly={true}
          onSaved={() => {
            setSelected(null);
            loadArchives();
          }}
        />
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E3EBF5] p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-[#66809E]">
            {label}
          </div>
          <div className="text-2xl font-bold text-[#102A4C] mt-1">
            {value}
          </div>
        </div>

        <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
          <Icon className="h-5 w-5 text-slate-600" />
        </div>
      </div>
    </div>
  );
}
